import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./TimeTrackingV2Screen.css";
import { MaterialSearchPicker } from "./MaterialSearchPicker";
import { loadMaterialsCatalog } from "../helpers/materialsCatalog";
import { getMaterialRelationName } from "../helpers/materialVisibility";
import { usePermissions } from "../hooks/usePermissions";
import {
  canEditTimeLogMaterials,
  commitTimeLogMaterialUsage,
  loadMaterialUsageForTimeLog,
  updateTimeLogMaterialUsage,
  voidTimeLogMaterialUsage,
} from "../helpers/projectMaterialLedger";
import {
  findMatchingQuoteLine,
  loadProjectQuoteLinesCached,
} from "../helpers/projectQuoteLineMatching";
import { QUOTE_LINE_TYPE } from "../helpers/projectQuoteSnapshots";
import { CPInput } from "./CPInput";

/**
 * Materials Used for one time log — same catalog/ledger as session materials.
 * Visibility/edit follow TIMELOG_EDIT on the parent log (not a separate permission).
 */
export function TimeLogMaterialsSection({ timeLog, readOnly = false }) {
  const { actor, orgPolicies, directReportIds } = usePermissions();
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addMaterialId, setAddMaterialId] = useState("");
  const [addQuantity, setAddQuantity] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editQuantity, setEditQuantity] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [materialQuoteLines, setMaterialQuoteLines] = useState([]);

  const canEdit =
    !readOnly && canEditTimeLogMaterials(actor, timeLog, { directReportIds });

  const selectedMaterial = useMemo(
    () => materials.find((item) => item.material_id === addMaterialId),
    [materials, addMaterialId],
  );

  const loadLines = useCallback(async () => {
    if (!timeLog?.id && !timeLog?.session_id) {
      setLines([]);
      return;
    }

    setLoading(true);
    try {
      const rows = await loadMaterialUsageForTimeLog(timeLog.id, {
        sessionId: timeLog.session_id || null,
        actor,
      });
      setLines(rows);
    } catch (error) {
      console.error("TIME LOG MATERIALS LOAD ERROR:", error);
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [timeLog?.id, timeLog?.session_id, actor]);

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    const relation = getMaterialRelationName(actor, orgPolicies);
    const { materials: catalog } = await loadMaterialsCatalog({
      onCached: (cached) => {
        setMaterials(cached);
        setLoadingCatalog(false);
      },
      relation,
      actor,
    });
    setMaterials(catalog || []);
    setLoadingCatalog(false);
  }, [actor, orgPolicies]);

  useEffect(() => {
    void loadLines();
  }, [loadLines]);

  useEffect(() => {
    if (!canEdit) return;
    void loadCatalog();
  }, [canEdit, loadCatalog]);

  useEffect(() => {
    let cancelled = false;
    const projectId = timeLog?.project_id;
    if (!projectId) {
      setMaterialQuoteLines([]);
      return undefined;
    }

    loadProjectQuoteLinesCached(projectId, { actor })
      .then((rows) => {
        if (cancelled) return;
        setMaterialQuoteLines(
          (rows || []).filter(
            (row) => row.line_type === QUOTE_LINE_TYPE.MATERIAL,
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setMaterialQuoteLines([]);
      });

    return () => {
      cancelled = true;
    };
  }, [timeLog?.project_id, actor]);

  function getQuoteHint(materialId) {
    if (!materialId || !materialQuoteLines.length) return null;
    const match = findMatchingQuoteLine(materialQuoteLines, materialId);
    if (!match) return null;
    return `Quoted: ${match.description_snapshot}`;
  }

  function resetAddForm() {
    setAddMaterialId("");
    setAddQuantity("");
    setAddNotes("");
    setShowAddForm(false);
  }

  async function handleAddMaterial() {
    if (!canEdit || !timeLog?.id) return;

    if (!addMaterialId) {
      alert("Select a material.");
      return;
    }

    const quantity = Number(addQuantity);
    if (!quantity || quantity <= 0) {
      alert("Enter a quantity greater than zero.");
      return;
    }

    if (!selectedMaterial) {
      alert("Selected material not found.");
      return;
    }

    if (!timeLog.project_id) {
      alert("Assign a project on this time log before adding materials.");
      return;
    }

    setBusy(true);
    try {
      await commitTimeLogMaterialUsage({
        actor,
        timeLog,
        material: selectedMaterial,
        quantity,
        notes: addNotes,
        directReportIds,
      });
      resetAddForm();
      await loadLines();
    } catch (error) {
      console.error("TIME LOG MATERIAL ADD ERROR:", error);
      alert(error.message || "Unable to add material.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveLine(usageId) {
    if (!canEdit || !usageId) return;
    if (!window.confirm("Remove this material from the time log?")) return;

    setBusy(true);
    try {
      await voidTimeLogMaterialUsage({
        actor,
        timeLog,
        usageId,
        directReportIds,
      });
      if (editingId === usageId) setEditingId(null);
      await loadLines();
    } catch (error) {
      console.error("TIME LOG MATERIAL REMOVE ERROR:", error);
      alert(error.message || "Unable to remove material.");
    } finally {
      setBusy(false);
    }
  }

  function startEditLine(line) {
    setEditingId(line.usage_id);
    setEditQuantity(String(line.quantity));
    setEditNotes(line.notes || "");
  }

  async function handleSaveEdit(usageId) {
    const quantity = Number(editQuantity);
    if (!quantity || quantity <= 0) {
      alert("Enter a quantity greater than zero.");
      return;
    }

    setBusy(true);
    try {
      await updateTimeLogMaterialUsage({
        actor,
        timeLog,
        usageId,
        quantity,
        notes: editNotes,
        directReportIds,
      });
      setEditingId(null);
      await loadLines();
    } catch (error) {
      console.error("TIME LOG MATERIAL UPDATE ERROR:", error);
      alert(error.message || "Unable to update material.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sw-materials-compact">
      <div className="sw-materials-compact-panel">
        <div className="sw-icon-row sw-materials-compact-header">
          <div className="sw-icon-row-body">
            <div className="sw-icon-row-label">Materials Used</div>
            {lines.length > 0 && (
              <div className="sw-icon-row-value">
                {lines.length} item{lines.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <p className="ci-review-row-empty sw-materials-empty">
            Loading materials…
          </p>
        ) : lines.length === 0 ? (
          <p className="ci-review-row-empty sw-materials-empty">
            No materials recorded
          </p>
        ) : (
          <div className="sw-materials-compact-list">
            {lines.map((line) => {
              const isEditing = editingId === line.usage_id;

              return (
                <div key={line.usage_id} className="sw-material-compact-row">
                  {isEditing && canEdit ? (
                    <div className="sw-material-compact-edit-form">
                      <div className="sw-material-compact-name">
                        {line.material_name_snapshot}
                      </div>
                      <CPInput
                        className="cp-input-group--compact"
                        label="Quantity"
                        type="number"
                        min="0"
                        step="any"
                        value={editQuantity}
                        onChange={setEditQuantity}
                        placeholder="0"
                        disabled={busy}
                      />
                      <CPInput
                        className="cp-input-group--compact"
                        label="Notes"
                        value={editNotes}
                        onChange={setEditNotes}
                        placeholder="Optional"
                        disabled={busy}
                      />
                      <div className="sw-inline-actions">
                        <button
                          type="button"
                          className="nx-btn sw-btn-secondary"
                          onClick={() => setEditingId(null)}
                          disabled={busy}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="nx-btn nx-btn-gold"
                          onClick={() => handleSaveEdit(line.usage_id)}
                          disabled={busy}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="sw-material-compact-main">
                        <span
                          className="sw-material-compact-name"
                          title={line.material_name_snapshot}
                        >
                          {line.material_name_snapshot}
                        </span>
                        {canEdit && (
                          <div className="sw-material-compact-actions">
                            <button
                              type="button"
                              className="sw-material-compact-edit"
                              onClick={() => startEditLine(line)}
                              disabled={busy}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="sw-material-compact-remove"
                              onClick={() => handleRemoveLine(line.usage_id)}
                              disabled={busy}
                              aria-label={`Remove ${line.material_name_snapshot}`}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="sw-material-compact-meta">
                        {Number(line.quantity)}{" "}
                        {line.unit_of_measure_snapshot || "units"}
                        {line.sku_snapshot ? ` · SKU ${line.sku_snapshot}` : ""}
                      </div>
                      {line.notes ? (
                        <div className="sw-material-compact-meta">
                          {line.notes}
                        </div>
                      ) : null}
                      {getQuoteHint(line.material_id) && (
                        <div
                          className="sw-material-compact-meta"
                          style={{ color: "#c9a84c" }}
                        >
                          {getQuoteHint(line.material_id)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {canEdit && timeLog?.id && (
          <>
            {showAddForm ? (
              <div className="sw-materials-add-form">
                {loadingCatalog ? (
                  <p className="ci-review-row-empty">Loading materials…</p>
                ) : (
                  <>
                    <MaterialSearchPicker
                      materials={materials}
                      value={addMaterialId}
                      onChange={setAddMaterialId}
                      disabled={loadingCatalog || busy}
                      showPricing={false}
                    />
                    {selectedMaterial?.unit_of_measure ? (
                      <p className="ci-review-row-empty" style={{ marginTop: 8 }}>
                        Unit of Measure: {selectedMaterial.unit_of_measure}
                      </p>
                    ) : null}
                    {getQuoteHint(addMaterialId) && (
                      <p
                        className="ci-review-row-empty"
                        style={{ color: "#c9a84c", marginTop: "8px" }}
                      >
                        {getQuoteHint(addMaterialId)}
                      </p>
                    )}

                    <CPInput
                      className="cp-input-group--compact"
                      label="Quantity"
                      type="number"
                      min="0"
                      step="any"
                      value={addQuantity}
                      onChange={setAddQuantity}
                      placeholder="0"
                      disabled={busy}
                    />

                    <CPInput
                      className="cp-input-group--compact"
                      label="Notes"
                      value={addNotes}
                      onChange={setAddNotes}
                      placeholder="Optional"
                      disabled={busy}
                    />

                    <div className="sw-inline-actions">
                      <button
                        type="button"
                        className="nx-btn sw-btn-secondary"
                        onClick={resetAddForm}
                        disabled={busy}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="nx-btn nx-btn-gold"
                        onClick={handleAddMaterial}
                        disabled={busy}
                      >
                        + Add Material
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="nx-btn sw-btn-secondary sw-btn-add-material-list"
                onClick={() => setShowAddForm(true)}
                disabled={busy || !timeLog.project_id}
              >
                + Add Material
              </button>
            )}
          </>
        )}

        {canEdit && timeLog?.id && !timeLog.project_id ? (
          <p className="ci-review-row-empty" style={{ marginTop: 8 }}>
            Assign a project to this time log before adding materials.
          </p>
        ) : null}
      </div>
    </div>
  );
}
